import type {
  DomainRouterConnection,
  DomainRouterJsonInput,
} from "@triedge/domain-core";
import type {
  Application,
  ApplicationDocument,
  ApplicationFilter,
  ApplicationNote,
  ApplicationQuickView,
  AssignApplicationInput,
  CalendarEvent,
  ChildApplication,
  CompleteEnrollmentInput,
  Contact,
  CreateNoteInput,
  DeleteNoteInput,
  RequestInterviewScheduleInput,
  ScheduleInterviewInput,
  ScheduleVisitInput,
  SendApplicationMessageInput,
  SubmitApplicationInput,
  TimelineEvent,
  UpdateApplicationStatusInput,
  UpdateChildApplicationInput,
  UpdateEnrollmentProgressInput,
  UpdateMedicalRecordInput,
  UpdateNoteInput,
  UpdateUserPreferencesInput,
  UploadDocumentInput,
  UpsertEnrollmentStepDataInput,
  UserPreferences,
  QaEnrollmentBaselineResetApplyResult,
  QaEnrollmentBaselineResetPreview,
  QaEnrollmentBaselineResetRollbackResult,
} from "./index";

export type LookupApplicationResponse = {
  found: boolean;
  application: Application | null;
};

export type ParentAdmissionsDraft = {
  draftId: string;
  parentActorId: string;
  currentStep: number;
  formState: Record<string, unknown>;
  createdAtEpochSeconds: number;
  updatedAtEpochSeconds: number;
  expiresAtEpochSeconds: number;
};

export type ParentAdmissionsDraftEnvelope = {
  draft: ParentAdmissionsDraft | null;
};

export type AdmissionsApplicationConnection =
  DomainRouterConnection<Application>;
export type AdmissionsDocumentConnection =
  DomainRouterConnection<ApplicationDocument>;
export type AdmissionsContactConnection = DomainRouterConnection<Contact>;
export type AdmissionsTimelineConnection =
  DomainRouterConnection<TimelineEvent>;
export type AdmissionsTuitionConfiguration = Record<string, unknown>;
export type AdmissionsTuitionConfigurationEnvelope = {
  configuration: AdmissionsTuitionConfiguration | null;
  documentId?: string | null;
};
export type AdmissionsTuitionResolvedChild = Record<string, unknown>;
export type AdmissionsTuitionResolution = {
  applicationId: string;
  academicYear: string;
  currentStatus?: string | null;
  family: Record<string, unknown>;
  students: Record<string, unknown>[];
  acceptedChildren: AdmissionsTuitionResolvedChild[];
};
export type SignedDownloadUrlResponse = {
  downloadUrl: string;
  fileName: string;
  contentType?: string | null;
  expiresAt: number;
};
export type AdmissionsTuitionContractGeneration = {
  configuration: AdmissionsTuitionConfiguration;
  contractDocument: ApplicationDocument;
};
export type AdmissionsTuitionContractSend = {
  configuration: AdmissionsTuitionConfiguration;
  contractDocument: ApplicationDocument;
  communication: TimelineEvent | null;
};

export const ADMISSIONS_QUERY_OPERATION_IDS = [
  "listApplications",
  "getApplication",
  "getApplicantApplications",
  "getApplicationDocuments",
  "getApplicationDocumentDownloadUrl",
  "getUserContacts",
  "lookupApplication",
  "listTimelineEvents",
  "getApplicationQuickView",
  "listCalendarEvents",
  "getUserPreferences",
  "resolveFamilyForAdmissionsApplication",
  "getAdmissionsTuitionConfiguration",
  "getParentAdmissionsDraft",
  "previewQaEnrollmentBaselineReset",
] as const;

export const ADMISSIONS_MUTATION_OPERATION_IDS = [
  "submitApplication",
  "updateApplicationStatus",
  "updateApplication",
  "assignApplication",
  "requestInterviewSchedule",
  "scheduleInterview",
  "scheduleVisit",
  "updateChildApplication",
  "updateEnrollmentProgress",
  "updateMedicalRecord",
  "upsertEnrollmentStepData",
  "completeEnrollment",
  "addNote",
  "updateNote",
  "deleteNote",
  "addDocument",
  "sendApplicationMessage",
  "updateUserPreferences",
  "upsertAdmissionsTuitionConfiguration",
  "generateTuitionContract",
  "sendTuitionContract",
  "upsertParentAdmissionsDraft",
  "deleteParentAdmissionsDraft",
  "applyQaEnrollmentBaselineReset",
  "rollbackQaEnrollmentBaselineReset",
] as const;

export type AdmissionsQueryOperationId =
  (typeof ADMISSIONS_QUERY_OPERATION_IDS)[number];
export type AdmissionsMutationOperationId =
  (typeof ADMISSIONS_MUTATION_OPERATION_IDS)[number];

export type AdmissionsQueryArgsMap = {
  listApplications: {
    filter?: ApplicationFilter | DomainRouterJsonInput;
    limit?: number;
    nextToken?: string | null;
    projection?: "full" | "dashboard";
  };
  getApplication: { id: string };
  getApplicantApplications: {
    applicantId: string;
    limit?: number;
    nextToken?: string | null;
  };
  getApplicationDocuments: {
    applicationId: string;
    limit?: number;
    nextToken?: string | null;
  };
  getApplicationDocumentDownloadUrl: {
    applicationId: string;
    documentId: string;
    disposition?: "attachment" | "inline";
  };
  getUserContacts: {
    userId: string;
    limit?: number;
    nextToken?: string | null;
  };
  lookupApplication: { code: string; familyName: string };
  listTimelineEvents: {
    filter?: DomainRouterJsonInput;
    limit?: number;
    nextToken?: string | null;
  };
  getApplicationQuickView: { applicationId: string };
  listCalendarEvents: { filter?: DomainRouterJsonInput };
  getUserPreferences: Record<string, never>;
  resolveFamilyForAdmissionsApplication: { applicationId: string };
  getAdmissionsTuitionConfiguration: { applicationId: string };
  getParentAdmissionsDraft: { parentActorId?: string };
  previewQaEnrollmentBaselineReset: { input: DomainRouterJsonInput };
};

export type AdmissionsQueryResultMap = {
  listApplications: AdmissionsApplicationConnection;
  getApplication: Application;
  getApplicantApplications: AdmissionsApplicationConnection;
  getApplicationDocuments: AdmissionsDocumentConnection;
  getApplicationDocumentDownloadUrl: SignedDownloadUrlResponse;
  getUserContacts: AdmissionsContactConnection;
  lookupApplication: LookupApplicationResponse;
  listTimelineEvents: AdmissionsTimelineConnection;
  getApplicationQuickView: ApplicationQuickView;
  listCalendarEvents: CalendarEvent[];
  getUserPreferences: UserPreferences;
  resolveFamilyForAdmissionsApplication: AdmissionsTuitionResolution;
  getAdmissionsTuitionConfiguration: AdmissionsTuitionConfigurationEnvelope;
  getParentAdmissionsDraft: ParentAdmissionsDraftEnvelope;
  previewQaEnrollmentBaselineReset: QaEnrollmentBaselineResetPreview;
};

export type AdmissionsMutationArgsMap = {
  submitApplication: SubmitApplicationInput | string | null | undefined;
  updateApplicationStatus: UpdateApplicationStatusInput | DomainRouterJsonInput;
  updateApplication: DomainRouterJsonInput;
  assignApplication: AssignApplicationInput | DomainRouterJsonInput;
  requestInterviewSchedule:
    | RequestInterviewScheduleInput
    | DomainRouterJsonInput;
  scheduleInterview: ScheduleInterviewInput | DomainRouterJsonInput;
  scheduleVisit: ScheduleVisitInput | DomainRouterJsonInput;
  updateChildApplication: UpdateChildApplicationInput | DomainRouterJsonInput;
  updateEnrollmentProgress:
    | UpdateEnrollmentProgressInput
    | DomainRouterJsonInput;
  updateMedicalRecord: UpdateMedicalRecordInput | DomainRouterJsonInput;
  upsertEnrollmentStepData:
    | UpsertEnrollmentStepDataInput
    | DomainRouterJsonInput;
  completeEnrollment: CompleteEnrollmentInput | DomainRouterJsonInput;
  addNote: CreateNoteInput | DomainRouterJsonInput;
  updateNote: UpdateNoteInput | DomainRouterJsonInput;
  deleteNote: DeleteNoteInput | DomainRouterJsonInput;
  addDocument: UploadDocumentInput | DomainRouterJsonInput;
  sendApplicationMessage: SendApplicationMessageInput | DomainRouterJsonInput;
  updateUserPreferences: UpdateUserPreferencesInput | DomainRouterJsonInput;
  upsertAdmissionsTuitionConfiguration: { input: DomainRouterJsonInput };
  generateTuitionContract: { input: DomainRouterJsonInput };
  sendTuitionContract: { input: DomainRouterJsonInput };
  upsertParentAdmissionsDraft: {
    parentActorId?: string;
    currentStep: number;
    formState: DomainRouterJsonInput;
  };
  deleteParentAdmissionsDraft: { parentActorId?: string };
  applyQaEnrollmentBaselineReset: { input: DomainRouterJsonInput };
  rollbackQaEnrollmentBaselineReset: { input: DomainRouterJsonInput };
};

export type AdmissionsMutationResultMap = {
  submitApplication: Application;
  updateApplicationStatus: Application;
  updateApplication: Application;
  assignApplication: Application;
  requestInterviewSchedule: Application;
  scheduleInterview: Application;
  scheduleVisit: Application;
  updateChildApplication: ChildApplication;
  updateEnrollmentProgress: ChildApplication;
  updateMedicalRecord: ChildApplication;
  upsertEnrollmentStepData: ChildApplication;
  completeEnrollment: Application;
  addNote: ApplicationNote;
  updateNote: ApplicationNote;
  deleteNote: boolean;
  addDocument: ApplicationDocument;
  sendApplicationMessage: TimelineEvent;
  updateUserPreferences: UserPreferences;
  upsertAdmissionsTuitionConfiguration: AdmissionsTuitionConfigurationEnvelope;
  generateTuitionContract: AdmissionsTuitionContractGeneration;
  sendTuitionContract: AdmissionsTuitionContractSend;
  upsertParentAdmissionsDraft: ParentAdmissionsDraft;
  deleteParentAdmissionsDraft: boolean;
  applyQaEnrollmentBaselineReset: QaEnrollmentBaselineResetApplyResult;
  rollbackQaEnrollmentBaselineReset: QaEnrollmentBaselineResetRollbackResult;
};
